import { FC } from "react"

import { useTranslation } from "react-i18next"

import { DropdownMenu } from "components/inputs/DropdownMenu"
import { LabeledInput } from "components/inputs/LabeledInput"
import { ToggleCard } from "components/inputs/ToggleCard"
import { SectionCard } from "components/settings/SectionCard"

import { POSITION_VALUES } from "./constants"
import { FormState, SetFormField } from "./types"
import { FormErrors } from "./validation"

interface PlacementSectionProps {
  form: FormState
  errors: FormErrors
  saving: boolean
  onFieldChange: SetFormField
}

/** Padding fields stay strings so "blank" can mean "keep the frontend default". */
const PADDING_FIELDS: { key: keyof FormState; labelKey: string; hintKey?: string; placeholder: string }[] = [
  { key: "verticalPadding", labelKey: "verticalPadding", placeholder: "20" },
  { key: "horizontalPadding", labelKey: "horizontalPadding", placeholder: "20" },
  {
    key: "sportsbookHorizontalPadding",
    labelKey: "sportsbookPadding",
    hintKey: "sportsbookPaddingHint",
    placeholder: "345"
  }
]

export const PlacementSection: FC<PlacementSectionProps> = ({ form, errors, saving, onFieldChange }) => {
  const { t } = useTranslation("translation", { useSuspense: false })

  const positionOptions = POSITION_VALUES.map((value) => ({
    value,
    name: t(`settings.liveChat.placement.positionOptions.${value}`)
  }))

  return (
    <SectionCard
      title={t("settings.liveChat.placement.title")}
      description={t("settings.liveChat.placement.description")}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ToggleCard
          label={t("settings.liveChat.placement.desktopOnly")}
          checked={form.desktopOnly}
          onChange={() => onFieldChange("desktopOnly", !form.desktopOnly)}
          disabled={saving}
        />
        <ToggleCard
          label={t("settings.liveChat.placement.hideOnSportsbook")}
          checked={form.hideOnSportsbook}
          onChange={() => onFieldChange("hideOnSportsbook", !form.hideOnSportsbook)}
          disabled={saving}
        />
      </div>
      <p className="-mt-2 text-xs text-gray-500 dark:text-gray-400">
        {t("settings.liveChat.placement.desktopOnlyHint")}
      </p>

      {form.provider === "inhouse" && (
        <p className="text-xs text-warning">{t("settings.liveChat.placement.inhouseNotice")}</p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <DropdownMenu
          title={t("settings.liveChat.placement.position")}
          options={positionOptions}
          value={form.position}
          onChange={(e: { target: { value: string } }) =>
            onFieldChange("position", e.target.value as FormState["position"])
          }
          hideEmptyOption
          disableEmptyOption
          disabled={saving}
        />
        {PADDING_FIELDS.map((field) => {
          const error = errors[field.key]

          return (
            <LabeledInput
              key={field.key}
              id={`live-chat-${field.key}`}
              label={t(`settings.liveChat.placement.${field.labelKey}`)}
              value={form[field.key] as string}
              onChange={(value) => onFieldChange(field.key, value as FormState[typeof field.key])}
              disabled={saving}
              type="number"
              placeholder={field.placeholder}
              error={error && t(`settings.liveChat.validation.${error}`)}
              hint={field.hintKey && t(`settings.liveChat.placement.${field.hintKey}`)}
            />
          )
        })}
      </div>
    </SectionCard>
  )
}
